import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { ReactTable, NoContent } from '../../components/shared';
import LoadingSpinner from '../../components/LoadingSpinner';
import NewFilterDrawer from '../../components/shared/FilterDrawer/NewFilterDrawer';
import { useGetMissingDataFeedQuery } from '../../services/MissingDataFeed/MissingDataFeedService';
import {
  useLazyGetMissingDataFeedSourcesQuery,
  useLazyGetMissingDataFeedJobTypesQuery,
} from '../../services/MissingDataFeed/MissingDataFeedSourcesService';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';
import { MISSING_DATA_FEED } from '../../constants/tableColumns';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../constants/FilterConstants';
import useSearchFilterParam from '../../hooks/useSearchFilterParam';
import useStyles from './style';
import { formatDate, formatNullValue, useSortBy, useHasSortableColumns, useExceptionReportingHandlers } from './utils';
import { useDispatch, useSelector } from 'react-redux';
import { resetMissingDataFeedFilters, setMissingDataFeedFilters } from '../../redux-slice/exceptionReportingFilters';

const SORT_DEFAULT_VALUE = 'date:DESC';

const MissingDataFeedTab = ({ onCountChange }) => {
  const classes = useStyles();
  const { DRAWER_TYPES, TITLE, FILTER_DATA } = FILTER_CONSTANTS;
  const dispatch = useDispatch();
  const { missingDataFeed } = useSelector((state) => state.exceptionReportingFilter);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const [sort, setSort] = useState(SORT_DEFAULT_VALUE);
  const [filterParams, setFilterParams] = useState(FILTER_CONSTANTS.FILTER_DATA.MISSING_DATA_FEED.INITIAL_PARAMS);

  const [getMissingDataFeedSources] = useLazyGetMissingDataFeedSourcesQuery();
  const [getMissingDataFeedJobTypes] = useLazyGetMissingDataFeedJobTypesQuery();

  useEffect(() => {
    const storedFilterParams = { ...FILTER_DATA.MISSING_DATA_FEED.INITIAL_PARAMS };
    storedFilterParams[FILTER_VALUES.SOURCE_NAME] = missingDataFeed[FILTER_VALUES.SOURCE_NAME] ?? [];
    storedFilterParams[FILTER_VALUES.JOB_TYPE] = missingDataFeed[FILTER_VALUES.JOB_TYPE] ?? [];
    storedFilterParams[FILTER_VALUES.DATE_RANGE_FILTER] = missingDataFeed[FILTER_VALUES.DATE_RANGE_FILTER] ?? {
      from: '',
      to: '',
    };
    setSort(missingDataFeed.sort || SORT_DEFAULT_VALUE);
    setCurrentPage(missingDataFeed.pageNumber || DEFAULT_PAGE);
    setPerPage(missingDataFeed.rowsPerPage || DEFAULT_PER_PAGE);
    setFilterParams(storedFilterParams);
  }, [
    missingDataFeed.sort,
    missingDataFeed.pageNumber,
    missingDataFeed.rowsPerPage,
    missingDataFeed[FILTER_VALUES.SOURCE_NAME],
    missingDataFeed[FILTER_VALUES.JOB_TYPE],
    missingDataFeed[FILTER_VALUES.DATE_RANGE_FILTER],
  ]);

  const getAPI = useCallback(
    (value) => {
      switch (value) {
        case FILTER_VALUES.SOURCE_NAME:
          return getMissingDataFeedSources;
        case FILTER_VALUES.JOB_TYPE:
          return getMissingDataFeedJobTypes;
        default:
          return null;
      }
    },
    [getMissingDataFeedSources, getMissingDataFeedJobTypes],
  );

  const filterData = useMemo(
    () =>
      FILTER_CONSTANTS.FILTER_DATA.MISSING_DATA_FEED.FILTER.map((item) => {
        const api = getAPI(item.value);
        return {
          ...item,
          api,
        };
      }),
    [getAPI],
  );

  const searchParams = useSearchFilterParam({
    filterKey: FILTER_CONSTANTS.FILTER_DATA.MISSING_DATA_FEED,
    filterParams,
    sort,
  });

  const { data, isLoading, isFetching, isError, error } = useGetMissingDataFeedQuery({
    page: currentPage,
    perPage,
    searchParams,
  });

  useEffect(() => {
    if (isError) {
      onCountChange(0);
      return;
    }

    if (data?.missingDataFeedIssues) {
      onCountChange(data.pagination?.total || 0);
    }
  }, [data?.pagination?.total, onCountChange, isError]);

  const columns = useMemo(() => MISSING_DATA_FEED(), []);

  const hasSortableColumns = useHasSortableColumns(columns);

  const sortBy = useSortBy(sort, SORT_DEFAULT_VALUE);

  const tableData = useMemo(() => {
    if (!data || !data.missingDataFeedIssues) {
      return [];
    }

    const issues = data.missingDataFeedIssues;

    return issues.map((value) => ({
      date: formatDate(value.date),
      source_name: formatNullValue(value.source_name),
      issue: formatNullValue(value.issue),
      job_id: formatNullValue(value.job_id),
      job_type: formatNullValue(value.job_type),
      status: formatNullValue(value.status),
      trk_staging: formatNullValue(value.trk_staging),
      num_rows_staging: formatNullValue(value.num_rows_staging),
      num_rows_main: formatNullValue(value.num_rows_main),
      unique_sublocations: formatNullValue(value.unique_sublocations),
    }));
  }, [data]);

  const { applyFilterHandler, resetFilterParams, handleSortChangeWithRedux, handlePageChange, handlePerPageChange } =
    useExceptionReportingHandlers({
      dispatch,
      reduxState: missingDataFeed,
      setFilterAction: setMissingDataFeedFilters,
      resetFilterAction: resetMissingDataFeedFilters,
      initialFilterParams: FILTER_DATA.MISSING_DATA_FEED.INITIAL_PARAMS,
      sortDefaultValue: SORT_DEFAULT_VALUE,
      setFilterParams,
      setSort,
      setCurrentPage,
      setPerPage,
    });

  return (
    <>
      <Box className={classes.searchFilterWrap}>
        <NewFilterDrawer
          filteredValues={filterParams}
          name={TITLE.FILTERS.toUpperCase()}
          title={TITLE.FILTERS}
          drawerType={DRAWER_TYPES.FILTER_DRAWER}
          onReset={resetFilterParams}
          onApplyFilter={applyFilterHandler}
          filterData={filterData}
          emptyFilterLength={JSON.stringify(FILTER_CONSTANTS.FILTER_DATA.MISSING_DATA_FEED.INITIAL_PARAMS).length}
        />
      </Box>
      <Box>
        {isLoading || isFetching ? (
          <LoadingSpinner />
        ) : isError ? (
          <NoContent
            title="No Missing Data Feed Found"
            desc={
              error?.data?.message ||
              error?.message ||
              'There are no missing data feeds available with the current filters.'
            }
          />
        ) : !data ? (
          <NoContent title="No Missing Data Feed Found" desc="No data received from the server." />
        ) : tableData.length === 0 ? (
          <NoContent
            title="No Missing Data Feed Found"
            desc="No missing data feeds match your current search criteria."
          />
        ) : (
          <Box className={hasSortableColumns ? classes.fixedWidthTable : ''}>
            <ReactTable
              columns={columns}
              data={tableData}
              pagination={true}
              manualPagination={true}
              pageNumberForTablePagination={currentPage - 1}
              perPage={perPage}
              totalCount={data?.pagination?.total || 0}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              isLoading={isFetching}
              sortable={true}
              onSortChange={handleSortChangeWithRedux}
              sortBy={sortBy}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default MissingDataFeedTab;
